<script>
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getContactMobile,
  getContactUserId,
  getVipBadgeStyle,
  getVipLevelLabel,
} from 'dashboard/helper/vipLevelHelper';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    Avatar,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    userId() {
      return getContactUserId(this.contact);
    },
    mobile() {
      return getContactMobile(this.contact);
    },
    mobileHref() {
      return this.mobile ? `tel:${this.mobile}` : '';
    },
    currentAccount() {
      const accountId = this.$store.getters.getCurrentAccountId;
      return this.$store.getters['accounts/getAccount'](accountId) || {};
    },
    vipLevelColors() {
      return this.currentAccount.settings?.vip_level_colors || {};
    },
    vipLevel() {
      return getVipLevelLabel(this.contact);
    },
    vipLevelStyle() {
      return {
        ...getVipBadgeStyle(this.contact, this.vipLevelColors),
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '999px',
        padding: '1px 8px',
        fontWeight: 600,
      };
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    async copyUserId() {
      const userId = String(this.userId || '');
      if (!userId) {
        useAlert('暂无可复制的用户ID');
        return;
      }
      await copyTextToClipboard(userId);
      useAlert('用户ID已复制');
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
          rounded-full
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center">
            <NextButton
              v-tooltip.top-end="'复制用户ID'"
              icon="i-lucide-copy"
              slate
              faded
              sm
              @click="copyUserId"
            />
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :value="userId"
            icon="contact-identify"
            emoji="🆔"
            :title="$t('CONTACT_PANEL.USER_ID')"
            show-copy
            show-title
          />
          <ContactInfoRow
            :href="mobileHref"
            :value="mobile"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.MOBILE')"
            show-copy
            show-title
          />
          <ContactInfoRow
            :value="vipLevel"
            icon="contact-identify"
            emoji="⭐"
            :title="$t('CONTACT_PANEL.VIP_LEVEL')"
            :value-style="vipLevelStyle"
            show-copy
            show-title
          />
        </div>
      </div>
    </div>
  </div>
</template>
